// src/features/reports/components/Reports/components/ReportsCharts.jsx
import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale,
  BarElement, ArcElement,
  LineElement, PointElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import styles from '../Reports.module.css';
import { formatNumber } from '../../../../../shared/utils/formatters';
import { TYPE_MAP } from '../hooks/useReportsData';

ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, ArcElement,
  LineElement, PointElement,
  Title, Tooltip, Legend, Filler
);

export default function ReportsCharts({ reportData, netProfit, totalExpenses }) {
  if (!reportData) return null;

  const isDark         = document.documentElement.getAttribute('data-theme') === 'dark';
  const chartTextColor = isDark ? '#a1a1aa' : '#64748b';
  const gridColor      = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  // إعدادات مشتركة لكل الشارتات
  const baseTooltip = {
    backgroundColor: isDark ? '#18181b' : '#fff',
    borderColor:     isDark ? '#3f3f46' : '#e2e8f0',
    borderWidth:     1,
    titleColor:      isDark ? '#fafafa' : '#0f172a',
    bodyColor:       isDark ? '#a1a1aa' : '#64748b',
    padding:         10,
  };

  // ── شارت 1: إيرادات / مصاريف / ربح ─────────────────────────────
  const revExpData = {
    labels: ['الإيرادات', 'المصاريف', 'صافي الربح'],
    datasets: [{
      data: [
        parseFloat(reportData.summary.total_revenue) || 0,
        totalExpenses,
        netProfit,
      ],
      backgroundColor: ['#0d948820', '#ef444420', '#3b82f620'],
      borderColor:     ['#0d9488',   '#ef4444',   '#3b82f6'  ],
      borderWidth:      2,
      borderRadius:     8,
      borderSkipped:    false,
    }],
  };

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend:  { display: false },
      tooltip: {
        ...baseTooltip,
        callbacks: {
          label: ctx => ` ${formatNumber(ctx.parsed.y, 0)} د.ع`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: chartTextColor },
        grid:  { display: false },
      },
      y: {
        ticks: { color: chartTextColor, callback: v => formatNumber(v, 0) },
        grid:  { color: gridColor },
      },
    },
  };

  // ── شارت 2: توزيع أنواع الحجوزات ───────────────────────────────
  const typeMap = {};
  reportData.bookings_details.forEach(b => {
    typeMap[b.booking_type] = (typeMap[b.booking_type] || 0) + 1;
  });

  const doughnutData = Object.keys(typeMap).length ? {
    labels:   Object.keys(typeMap).map(t => TYPE_MAP[t] || t),
    datasets: [{
      data:            Object.values(typeMap),
      backgroundColor: ['#0d9488','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#10b981'],
      borderWidth:     0,
      hoverOffset:     6,
    }],
  } : null;

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        display:  true,
        position: 'bottom',
        labels:   { color: chartTextColor, padding: 10, font: { size: 11 } },
      },
      tooltip: {
        ...baseTooltip,
        callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}` },
      },
    },
  };

  // ── شارت 3: المدفوع vs المتبقي ──────────────────────────────────
  const sorted = [...reportData.bookings_details]
    .sort((a, b) => a.booking_date.localeCompare(b.booking_date))
    .slice(0, 30);

  const lineData = sorted.length ? {
    labels: sorted.map(b => b.booking_date),
    datasets: [
      {
        label:                'مدفوع',
        data:                 sorted.map(b => parseFloat(b.total_paid) || 0),
        borderColor:          '#0d9488',
        backgroundColor:      '#0d948818',
        fill:                 true,
        tension:              0.4,
        pointRadius:          3,
        pointBackgroundColor: '#0d9488',
        borderWidth:          2,
      },
      {
        label:                'متبقي',
        data:                 sorted.map(b => parseFloat(b.remaining_amount) || 0),
        borderColor:          '#f59e0b',
        backgroundColor:      '#f59e0b12',
        fill:                 true,
        tension:              0.4,
        pointRadius:          3,
        pointBackgroundColor: '#f59e0b',
        borderWidth:          2,
      },
    ],
  } : null;

  const lineOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display:  true,
        position: 'top',
        labels:   { color: chartTextColor, padding: 12, font: { size: 11 } },
      },
      tooltip: {
        ...baseTooltip,
        callbacks: { label: ctx => ` ${ctx.dataset.label}: ${formatNumber(ctx.parsed.y, 0)} د.ع` },
      },
    },
    scales: {
      x: {
        ticks: { color: chartTextColor, maxTicksLimit: 8, maxRotation: 0 },
        grid:  { display: false },
      },
      y: {
        ticks: { color: chartTextColor, callback: v => formatNumber(v, 0) },
        grid:  { color: gridColor },
      },
    },
  };

  return (
    <div className={styles.chartsRow}>

      {/* شارت الأعمدة */}
      <div className={styles.chartCard}>
        <p className={styles.chartTitle}>الإيرادات، المصاريف، الربح</p>
        <div className={styles.chartWrap} style={{ height: 220 }}>
          <Bar data={revExpData} options={barOpts} />
        </div>
      </div>

      {/* شارت الدائرة */}
      <div className={styles.chartCard}>
        <p className={styles.chartTitle}>توزيع أنواع الحجوزات</p>
        <div className={styles.chartWrap} style={{ height: 220 }}>
          {doughnutData
            ? <Doughnut data={doughnutData} options={doughnutOpts} />
            : <p style={{ textAlign: 'center', color: 'var(--text-muted)', paddingTop: '4rem', fontSize: '13px' }}>
                لا توجد بيانات
              </p>}
        </div>
      </div>

      {/* شارت الخط */}
      <div className={`${styles.chartCard} ${styles.chartWide}`}>
        <p className={styles.chartTitle}>المدفوع مقابل المتبقي (آخر 30 حجز)</p>
        <div className={styles.chartWrap} style={{ height: 220 }}>
          {lineData
            ? <Line data={lineData} options={lineOpts} />
            : <p style={{ textAlign: 'center', color: 'var(--text-muted)', paddingTop: '4rem', fontSize: '13px' }}>
                لا توجد بيانات
              </p>}
        </div>
      </div>

    </div>
  );
}